import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { getCategoryName } from '../../utils/categoryNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOBILE_INITIAL_COUNT = 6;

const CategoryTile = ({ cat, compact }) => {
  const { t } = useTranslation();
  const IconComponent = cat.icon;
  const hasCustomIcon = cat.icon_url;
  const circleSize = compact ? 'w-12 h-12 md:w-14 md:h-14' : 'w-16 h-16';
  const iconPx = compact ? 20 : 32;
  const imgSize = compact ? 'w-7 h-7' : 'w-10 h-10';

  return (
    <Link
      to={`/browse?category=${cat.value}`}
      className="flex flex-col items-center text-center group"
      data-testid={`category-tile-${cat.value}`}
    >
      <div className={`category-circle ${circleSize} rounded-full bg-white border-2 border-accent flex items-center justify-center mb-2 shadow-sm group-hover:border-primary group-hover:shadow-md transition-all overflow-hidden`}>
        {hasCustomIcon ? (
          <img src={`${API}/files/${cat.icon_url}`} alt={getCategoryName(cat.value, t)} className={`${imgSize} object-contain`} />
        ) : (
          <IconComponent size={iconPx} weight="regular" className="category-icon text-primary" />
        )}
      </div>
      <p className={`font-medium text-text_main group-hover:text-primary transition-colors leading-tight ${compact ? 'text-xs' : 'text-sm md:text-base min-h-[2.5rem] flex items-center justify-center'}`}>
        {getCategoryName(cat.value, t)}
      </p>
    </Link>
  );
};

/* Unified Categories section: fixed 6-col grid (3 rows) on tablet+desktop
   (all categories, no toggle), 6-then-expand 3-col grid on mobile. Sort
   order (manual vs auto-by-active-count) is decided upstream in
   LandingPage.js — this component just renders whatever order it's given. */
export const CategoriesSection = ({ categories }) => {
  const { t } = useTranslation();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  if (!categories.length) return null;

  const mobileVisible = mobileExpanded ? categories : categories.slice(0, MOBILE_INITIAL_COUNT);
  const showToggle = categories.length > MOBILE_INITIAL_COUNT;

  return (
    <div className="pt-10 md:pt-14 pb-12 md:pb-16" data-testid="categories-section">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-lg md:text-xl font-medium text-center mb-8 text-text_strong tracking-wide">
          {t('home.allCategories')}
        </h2>

        {/* Mobile: 3-col grid, 6 initial (most active), expandable */}
        <div className="md:hidden" data-testid="categories-mobile">
          <div className="grid grid-cols-3 gap-6">
            {mobileVisible.map((cat) => (
              <CategoryTile key={cat.value} cat={cat} compact={mobileExpanded} />
            ))}
          </div>
          {showToggle && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setMobileExpanded((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                data-testid="categories-toggle-btn"
              >
                {mobileExpanded ? t('home.showLessCategories') : t('home.seeAllCategories')}
                {mobileExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Tablet + Desktop (≥768px): fixed 6-col grid, 3 rows, ALL categories, no toggle */}
        <div className="hidden md:grid md:grid-cols-6 gap-x-4 gap-y-8" data-testid="categories-desktop">
          {categories.map((cat) => (
            <CategoryTile key={cat.value} cat={cat} compact />
          ))}
        </div>
      </div>
    </div>
  );
};
